import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Spinner,
  Badge,
  Button,
} from "react-bootstrap";
import {
  FaTrophy,
  FaArrowRight,
  FaCheckCircle,
  FaTimesCircle,
  FaUserGraduate,
  FaChartLine,
  FaMedal,
  FaUsers,
  FaCalendarAlt,
  FaClock,
  FaTimes,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import CustomAsyncDropdown from "../../components/common/NewCustomAsyncSelectDropdown";
import NewCustomDataTable from "../../components/common/NewCustomDataTable";
import useScrollLoadMore from "../common/useScrollLoadMore";
import { getBatchOptions } from "../../../services/BatchService";
import { getMe } from "../../../services/AuthService";
import { getBatchRanking } from "../../../services/RankingService";
import debounce from "lodash/debounce";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useChartTheme } from "../../../utils/chartTheme";
import "../Trainer/TrainerDashboard.css";
import "../Dashboard/BusinessPulse.css";
import FilterSheet from "../common/FilterSheet/FilterSheet";

/* Only the pieces this chart uses. Registering the whole bundle pulls in
   every scale and controller Chart.js ships. */
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

const RankingDashboard = () => {
  const navigate = useNavigate();
  // The chart's colours, from the palette (utils/chartTheme). At the top
  // with the other hooks: the chart itself is built far below, past code a
  // hook must not sit behind.
  const chartTheme = useChartTheme();
  const [loading, setLoading] = useState(true);
  const [loadingRanking, setLoadingRanking] = useState(false);
  const [error, setError] = useState(null);

  const [batchOptions, setBatchOptions] = useState([]);
  const [selectedBatchOption, setSelectedBatchOption] = useState(null);
  const selectedBatchId = selectedBatchOption?.value || "";

  const [rankings, setRankings] = useState([]);
  const [topThree, setTopThree] = useState([]);
  const [currentStudentRanking, setCurrentStudentRanking] = useState(null);
  const [hasQuizzes, setHasQuizzes] = useState(true);
  const [batchInfo, setBatchInfo] = useState(null);
  const [viewType, setViewType] = useState(null);

  // ─── API pagination (used for admin/trainer server-side fetch) ────────────
  const [apiPagination, setApiPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const isStudent = useMemo(
    () => userRole?.toLowerCase() === "student",
    [userRole],
  );
  const isAdminOrTrainer = useMemo(
    () => userRole === "admin" || userRole === "trainer",
    [userRole],
  );

  // Determine if current view is student view based on viewType from API
  const isStudentView = useMemo(() => viewType === "student_view", [viewType]);
  const isFullView = useMemo(() => viewType === "full_view", [viewType]);

  // ─── Batch dropdown states ────────────────────────────────────────────────
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchPage, setBatchPage] = useState(1);
  const [batchHasMore, setBatchHasMore] = useState(true);
  const [batchSearchTerm, setBatchSearchTerm] = useState("");
  const [hasLoadedInitial, setHasLoadedInitial] = useState(false);
  const isFetchingBatches = useRef(false);

  // ─── NewCustomDataTable states (admin/trainer only) ───────────────────────
  const [tableCurrentPage, setTableCurrentPage] = useState(1);

  /* The row the click-through popup is showing, and the pane the
     scroll listener watches. */
  const [openRow, setOpenRow] = useState(null);
  const listRef = useRef(null);
  // The foot under the list -- the sentinel, in the flow of the page.
  const listEndRef = useRef(null);
  // A scroll batch, as against a fresh load. Separate from loadingRanking
  // because the table veils itself for that one and must not for this.
  const [loadingMoreRanking, setLoadingMoreRanking] = useState(false);
  const [tableRecordsPerPage, setTableRecordsPerPage] = useState(10);
  const [tableSearchTerm, setTableSearchTerm] = useState("");
  const [tableSelectedField, setTableSelectedField] = useState("studentName");

  const fetchBatches = useCallback(async (search = "", pageNum = 1) => {
    try {
      isFetchingBatches.current = true;
      // Names only: the picker reads id and name, nothing else.
      const resp = await getBatchOptions(10, pageNum, search);
      const batches = resp?.data || [];
      const hasMore = batches.length === 10;
      return { batches, hasMore };
    } catch (err) {
      console.error("Error fetching batches:", err);
      setError("Failed to load batches");
      return { batches: [], hasMore: false };
    } finally {
      isFetchingBatches.current = false;
    }
  }, []);

  const loadMoreBatches = useCallback(async () => {
    if (batchLoading || !batchHasMore || isFetchingBatches.current) return;
    setBatchLoading(true);
    const nextPage = batchPage + 1;
    const { batches, hasMore: more } = await fetchBatches(
      batchSearchTerm,
      nextPage,
    );
    if (batches.length > 0) {
      const newOptions = batches.map((batch) => ({
        value: batch.id,
        label: batch.title || batch.name || batch.id,
        data: batch,
      }));
      setBatchOptions((prev) => [...prev, ...newOptions]);
      setBatchPage(nextPage);
      setBatchHasMore(more);
    } else {
      setBatchHasMore(false);
    }
    setBatchLoading(false);
  }, [batchLoading, batchHasMore, batchPage, batchSearchTerm, fetchBatches]);

  const handleBatchSearch = useMemo(
    () =>
      debounce(async (searchValue) => {
        setBatchSearchTerm(searchValue);
        setBatchPage(1);
        setBatchHasMore(true);
        setBatchLoading(true);
        const { batches, hasMore: more } = await fetchBatches(searchValue, 1);
        const newOptions = batches.map((batch) => ({
          value: batch.id,
          label: batch.title || batch.name || batch.id,
          data: batch,
        }));
        setBatchOptions(newOptions);
        setBatchHasMore(more);
        setBatchLoading(false);
      }, 700),
    [fetchBatches],
  );

  const handleRefresh = useCallback(async () => {
    setBatchSearchTerm("");
    setBatchPage(1);
    setBatchHasMore(true);
    setHasLoadedInitial(false);
    setBatchLoading(true);
    const { batches, hasMore: more } = await fetchBatches("", 1);
    const newOptions = batches.map((batch) => ({
      value: batch.id,
      label: batch.title || batch.name || batch.id,
      data: batch,
    }));
    setBatchOptions(newOptions);
    setBatchHasMore(more);
    setBatchLoading(false);
    setHasLoadedInitial(true);
  }, [fetchBatches]);

  const handleBatchMenuOpen = useCallback(async () => {
    if (!hasLoadedInitial && batchOptions.length === 0 && !batchLoading) {
      setBatchLoading(true);
      setBatchPage(1);
      setBatchSearchTerm("");
      const { batches, hasMore: more } = await fetchBatches("", 1);
      const newOptions = batches.map((batch) => ({
        value: batch.id,
        label: batch.title || batch.name || batch.id,
        data: batch,
      }));
      setBatchOptions(newOptions);
      setBatchHasMore(more);
      setBatchLoading(false);
      setHasLoadedInitial(true);
    }
  }, [batchOptions.length, batchLoading, fetchBatches, hasLoadedInitial]);

  // ─── Load current user ────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const me = await getMe();
        const userData = me?.data || null;
        setCurrentUser(userData);
        const role = userData?.profiles?.[0]?.label?.toLowerCase() || null;
        setUserRole(role);
      } catch (e) {
        setError(e?.message || "Failed to load user data");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ─── Load rankings — triggers on batch only (not on pagination changes) ───
  useEffect(() => {
    const loadRanking = async () => {
      if (!selectedBatchId) return;
      try {
        setLoadingRanking(true);
        setError(null);

        const resp = await getBatchRanking(selectedBatchId, 1, 10);
        const data = resp?.data || {};

        setHasQuizzes(data.hasQuizzes !== false);
        setViewType(data.viewType || null);
        setBatchInfo(data.batchInfo || null);

        // Use the topThree array directly from API – it already contains the top 3 students with rank values
        const apiTopThree = data.topThree || [];
        setTopThree(apiTopThree);

        if (data.viewType === "student_view") {
          setCurrentStudentRanking(data.currentStudentRanking || null);
          setRankings([]);
          setApiPagination({
            page: 1,
            limit: 10,
            total: data.currentStudentRanking?.totalStudents || 0,
            totalPages: 1,
          });
          setTableCurrentPage(1);
        } else if (data.viewType === "full_view") {
          // Use the full rankings array from API – each entry already has rank
          setRankings(data.rankings || []);
          setCurrentStudentRanking(null);
          setApiPagination({
            page: 1,
            limit: 10,
            total: resp?.pagination?.total || 0,
            totalPages: resp?.pagination?.totalPages || 0,
          });
          setTableCurrentPage(1);
        } else {
          if (isStudent) {
            setCurrentStudentRanking(data.currentStudentRanking || null);
            setRankings([]);
            setApiPagination({
              page: 1,
              limit: 10,
              total: data.currentStudentRanking?.totalStudents || 0,
              totalPages: 1,
            });
          } else {
            setRankings(data.rankings || []);
            setCurrentStudentRanking(null);
            setApiPagination({
              page: 1,
              limit: 10,
              total: resp?.pagination?.total || 0,
              totalPages: resp?.pagination?.totalPages || 0,
            });
            setTableCurrentPage(1);
          }
        }
      } catch (e) {
        setError(e?.message || "Failed to load ranking");
      } finally {
        setLoadingRanking(false);
      }
    };
    loadRanking();
  }, [selectedBatchId, isStudent]);

  // Separate effect for loading rankings data when pagination changes
  useEffect(() => {
    const loadRankingsData = async () => {
      if (!selectedBatchId || !isFullView || isStudentView) return;

      // Past page 1 the request came from the scroll, so the rows already
      // read stay and the batch is added under them. This replaced the
      // array on every page, which meant the ten rows on screen were swapped
      // for the next ten rather than the list growing.
      const isAppend = apiPagination.page > 1;
      try {
        if (isAppend) setLoadingMoreRanking(true);
        else setLoadingRanking(true);

        const resp = await getBatchRanking(
          selectedBatchId,
          apiPagination.page,
          apiPagination.limit,
        );
        const data = resp?.data || {};

        if (data.viewType === "full_view") {
          const batch = data.rankings || [];
          setRankings((prev) => {
            if (!isAppend) return batch;
            // A student can land in two batches if the ranking shifts between
            // the two requests; rank is unique per student, id is the row.
            const seen = new Set(prev.map((r) => r.studentId ?? r.id ?? r.rank));
            return [...prev, ...batch.filter((r) => !seen.has(r.studentId ?? r.id ?? r.rank))];
          });
          setApiPagination((prev) => ({
            ...prev,
            total: resp?.pagination?.total || 0,
            totalPages: resp?.pagination?.totalPages || 0,
          }));
        }
      } catch (e) {
        console.error("Failed to load rankings data:", e);
        setError(e?.message || "Failed to load rankings");
      } finally {
        setLoadingRanking(false);
        setLoadingMoreRanking(false);
      }
    };

    if (
      selectedBatchId &&
      isFullView &&
      !isStudentView &&
      apiPagination.page > 0
    ) {
      loadRankingsData();
    }
  }, [
    selectedBatchId,
    apiPagination.page,
    apiPagination.limit,
    isFullView,
    isStudentView,
  ]);

  // ─── Batch change — reset everything ─────────────────────────────────────
  const handleBatchChange = (selectedOptions) => {
    const selectedOption =
      selectedOptions && selectedOptions.length > 0 ? selectedOptions[0] : null;
    setSelectedBatchOption(selectedOption);

    // Reset table states
    setTableCurrentPage(1);
    setTableSearchTerm("");
    setTableSelectedField("studentName");

    // Clear rankings data
    setRankings([]);

    // Reset API pagination
    setApiPagination({ page: 1, limit: 10, total: 0, totalPages: 0 });

    if (!selectedOption) {
      setTopThree([]);
      setCurrentStudentRanking(null);
      setHasQuizzes(true);
      setBatchInfo(null);
      setViewType(null);
    }
  };

  /* One dropdown, rendered into the gate before a batch is picked and
     into the strip afterwards. Declared below every handler it reads so
     nothing is referenced before it is initialised. */
  const batchPicker = (
    <CustomAsyncDropdown
      options={batchOptions}
      value={selectedBatchOption ? [selectedBatchOption] : []}
      onChange={handleBatchChange}
      placeholder="Search and select a batch…"
      isMulti={false}
      isSearchable={true}
      isLoading={batchLoading}
      onInputChange={handleBatchSearch}
      onMenuScrollToBottom={loadMoreBatches}
      onMenuOpen={handleBatchMenuOpen}
    />
  );

  const handleAttemptQuiz = () => {
    navigate("/student-quizzes", {
      state: { batchId: selectedBatchId, fromRanking: true },
    });
  };

  const getRankLabel = (rank) => {
    switch (rank) {
      case 1:
        return {
          position: "1st",
          badge: "🥇",
          color: "var(--gold)",
          bgColor: "var(--gold)",
          trophyBg: "linear-gradient(135deg, var(--gold) 0%, var(--saffron-deep) 100%)",
          textColor: "#fff",
        };
      case 2:
        return {
          position: "2nd",
          badge: "🥈",
          color: "#9CA3AF",
          bgColor: "#9CA3AF",
          trophyBg: "linear-gradient(135deg, #C7CCD3 0%, #9CA3AF 100%)",
          textColor: "#fff",
        };
      case 3:
        return {
          position: "3rd",
          badge: "🥉",
          color: "var(--saffron-deep)",
          bgColor: "var(--saffron-deep)",
          trophyBg: "linear-gradient(135deg, var(--saffron) 0%, var(--saffron-deep) 100%)",
          textColor: "#fff",
        };
      default:
        return null;
    }
  };

  const formatQuizScore = (score) => (score && score > 0 ? `${score}%` : "N/A");
  const formatAttendance = (percentage) =>
    percentage !== undefined && percentage > 0 ? `${percentage}%` : "N/A";

  // Simplified podium rendering using the rank from API
  const renderPodiumView = () => {
    if (!topThree || topThree.length === 0) {
      return (
        <div className="text-center py-5">
          <FaMedal
            size={48}
            className="text-muted mb-3"
            style={{ opacity: 0.5 }}
          />
          <p className="text-muted mb-0" style={{ fontSize: "16px" }}>
            No ranking data available for this batch.
          </p>
        </div>
      );
    }

    // Prepare podium positions: we need exactly 3 students.
    // The API already gives us topThree, but we must ensure they are ordered by rank.
    // Sort by rank ascending (1,2,3)
    const sortedTopThree = [...topThree].sort((a, b) => a.rank - b.rank);
    // Now map to display order: 2nd, 1st, 3rd
    const secondPlace = sortedTopThree.find((s) => s.rank === 2);
    const firstPlace = sortedTopThree.find((s) => s.rank === 1);
    const thirdPlace = sortedTopThree.find((s) => s.rank === 3);
    // Fill missing positions if any (shouldn't happen normally)
    const orderedPodium = [];
    if (secondPlace) orderedPodium.push(secondPlace);
    if (firstPlace) orderedPodium.push(firstPlace);
    if (thirdPlace) orderedPodium.push(thirdPlace);
    // If still less than 3, fill with remaining students
    if (orderedPodium.length < 3) {
      const remaining = sortedTopThree.filter(
        (s) => !orderedPodium.includes(s),
      );
      while (orderedPodium.length < 3 && remaining.length) {
        orderedPodium.push(remaining.shift());
      }
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          gap: "20px",
          padding: "0 20px",
          flexWrap: "wrap",
        }}
      >
        {orderedPodium.map((student, idx) => {
          const rank = student.rank;
          const rankLabel = getRankLabel(rank);
          if (!rankLabel) return null;
          const isCenter = rank === 1;
          const barHeight = isCenter ? "310px" : rank === 2 ? "280px" : "260px";

          return (
            <div
              key={student.studentId || `podium-${rank}-${idx}`}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flex: "0 1 250px",
                minWidth: "200px",
                maxWidth: "280px",
              }}
            >
              {isCenter && (
                <div
                  style={{
                    fontSize: "60px",
                    filter: `drop-shadow(0 4px 8px ${rankLabel.bgColor}80)`,
                  }}
                >
                  🏆
                </div>
              )}
              <div
                className="fw-bold text-white px-3 py-1 rounded shadow mb-2"
                style={{
                  backgroundColor: rankLabel.bgColor,
                  fontSize: "18px",
                  color: rankLabel.textColor,
                  textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                }}
              >
                {rankLabel.badge} {rankLabel.position}
              </div>
              <div
                style={{
                  width: "100%",
                  height: barHeight,
                  background: "var(--card)",
                  borderRadius: "12px 12px 0 0",
                  boxShadow: "var(--shadow-sm)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  padding: "20px 15px",
                  borderTop: `4px solid ${rankLabel.bgColor}`,
                  borderLeft: "1px solid var(--line)",
                  borderRight: "1px solid var(--line)",
                  position: "relative",
                }}
              >
                <p
                  className="fw-bold text-center"
                  style={{
                    fontSize: isCenter ? "20px" : "18px",
                    color: "var(--ink)",
                    marginBottom: "4px",
                  }}
                >
                  {student.studentName}
                </p>
                <div className="text-center" style={{ marginBottom: "15px" }}>
                  <div
                    className="fw-bold"
                    style={{
                      color: "var(--ink)",
                      fontSize: isCenter ? "28px" : "24px",
                      marginBottom: "2px",
                    }}
                  >
                    {formatQuizScore(student.avgQuizScore)}
                  </div>
                  <p
                    className="mb-0"
                    style={{ fontSize: "12px", color: "var(--ink)" }}
                  >
                    Quiz Average
                  </p>
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    gap: "8px",
                    marginBottom: "10px",
                  }}
                >
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <div
                      className="fw-semibold"
                      style={{
                        fontSize: "14px",
                        color: "var(--ink)",
                        marginBottom: "2px",
                      }}
                    >
                      {student.quizAttempted ? "Yes" : "No"}
                    </div>
                    <p
                      className="mb-0"
                      style={{ fontSize: "11px", color: "var(--ink)" }}
                    >
                      Attempted
                    </p>
                  </div>
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <div
                      className="fw-semibold"
                      style={{
                        fontSize: "14px",
                        color: "var(--ink)",
                        marginBottom: "2px",
                      }}
                    >
                      {student.quizPassed
                        ? "Passed"
                        : student.quizAttempted
                          ? "Failed"
                          : "N/A"}
                    </div>
                    <p
                      className="mb-0"
                      style={{ fontSize: "11px", color: "var(--ink)" }}
                    >
                      Status
                    </p>
                  </div>
                </div>
                <div style={{ width: "100%", textAlign: "center" }}>
                  <div
                    className="fw-semibold"
                    style={{
                      fontSize: "14px",
                      color: "var(--ink)",
                      marginBottom: "2px",
                    }}
                  >
                    {formatAttendance(student.attendancePercentage)}
                  </div>
                  <p
                    className="mb-0"
                    style={{ fontSize: "11px", color: "var(--ink)" }}
                  >
                    Attendance
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderStudentRankingCard = () => {
    if (!currentStudentRanking) return null;
    const rankLabel =
      currentStudentRanking.rank <= 3
        ? getRankLabel(currentStudentRanking.rank)
        : null;

    return (
      <Card className="student-ranking-card mb-4">
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-4">
            <div className="d-flex align-items-center gap-4">
              <div className="ranking-badge-wrapper">
                {rankLabel ? (
                  <div className="text-center">
                    <div className="rank-badge-lg mb-2">{rankLabel.badge}</div>
                    <div
                      className="fw-bold px-4 py-2 rounded"
                      style={{
                        backgroundColor: rankLabel.bgColor,
                        color: rankLabel.textColor,
                        fontSize: "24px",
                      }}
                    >
                      #{currentStudentRanking.rank}
                    </div>
                  </div>
                ) : (
                  <div
                    className="fw-bold px-4 py-2 rounded text-center"
                    style={{
                      backgroundColor: "#e9ecef",
                      color: "#495057",
                      fontSize: "24px",
                    }}
                  >
                    #{currentStudentRanking.rank}
                  </div>
                )}
              </div>
              <div>
                <h4 className="mb-1 text-dark">Your Performance</h4>
                <p className="text-muted mb-2">
                  Ranked #{currentStudentRanking.rank} out of{" "}
                  {currentStudentRanking.totalStudents || apiPagination.total}{" "}
                  students
                </p>
                <div className="d-flex flex-wrap gap-3 mt-3">
                  <div className="d-flex align-items-center gap-2">
                    <FaChartLine style={{ color: "var(--gold)" }} />
                    <span>
                      <strong>Quiz Score:</strong>{" "}
                      {formatQuizScore(currentStudentRanking.avgQuizScore)}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <FaCalendarAlt style={{ color: "var(--indigo)" }} />
                    <span>
                      <strong>Attendance:</strong>{" "}
                      {formatAttendance(
                        currentStudentRanking.attendancePercentage,
                      )}
                    </span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <FaClock style={{ color: "var(--teal)" }} />
                    <span>
                      <strong>Attempted:</strong>{" "}
                      {currentStudentRanking.attemptedQuizzes || 0}/
                      {currentStudentRanking.totalQuizzes || 0} quizzes
                    </span>
                  </div>
                </div>
              </div>
            </div>
            <Button
              variant="primary"
              onClick={handleAttemptQuiz}
              className="px-4 py-2 fw-bold"
              style={{ borderRadius: "8px" }}
            >
              Attempt Quiz <FaArrowRight size={16} className="ms-2" />
            </Button>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderStudentDetailsTable = () => {
    if (!currentStudentRanking) return null;
    const rankLabel =
      currentStudentRanking.rank <= 3
        ? getRankLabel(currentStudentRanking.rank)
        : null;

    return (
      <Card className="ranking-table-card mt-4">
        <Card.Header className="fw-bold bg-white border-bottom">
          <div className="d-flex align-items-center gap-2">
            <FaUserGraduate size={20} style={{ color: "var(--saffron)" }} />
            <h6 className="mb-0 fw-semibold" style={{ color: "var(--ink)" }}>
              Your Ranking Details
            </h6>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table hover mb-0 ranking-table">
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th style={{ width: "80px" }} className="text-center">
                    RANK
                  </th>
                  <th>STUDENT</th>
                  <th style={{ width: "140px" }} className="text-center">
                    QUIZ AVG
                  </th>
                  <th style={{ width: "160px" }} className="text-center">
                    ATTENDANCE
                  </th>
                  <th style={{ width: "140px" }} className="text-center">
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ backgroundColor: "#fff9f0" }}>
                  <td className="text-center align-middle">
                    {currentStudentRanking.rank <= 3 ? (
                      <div
                        className="fw-bold d-inline-block px-3 py-1 rounded"
                        style={{
                          backgroundColor: rankLabel.bgColor,
                          fontSize: "14px",
                          color: rankLabel.textColor,
                        }}
                      >
                        {rankLabel.badge} #{currentStudentRanking.rank}
                      </div>
                    ) : (
                      <span className="fw-bold">
                        #{currentStudentRanking.rank}
                      </span>
                    )}
                  </td>
                  <td className="align-middle">
                    <div className="fw-bold text-dark">
                      {currentUser?.name || currentStudentRanking.studentName}
                      <Badge
                        bg="warning"
                        className="ms-2"
                        style={{ fontSize: "11px" }}
                      >
                        You
                      </Badge>
                    </div>
                    {currentUser?.email && (
                      <small className="text-muted">{currentUser.email}</small>
                    )}
                  </td>
                  <td className="text-center align-middle">
                    <Badge
                      bg={
                        currentStudentRanking.avgQuizScore >= 75
                          ? "success"
                          : currentStudentRanking.avgQuizScore >= 50
                            ? "warning"
                            : "danger"
                      }
                      className="px-3 py-2"
                    >
                      {formatQuizScore(currentStudentRanking.avgQuizScore)}
                    </Badge>
                  </td>
                  <td className="text-center align-middle">
                    <span className="fw-semibold">
                      {formatAttendance(
                        currentStudentRanking.attendancePercentage,
                      )}
                    </span>
                    <br />
                    <small className="text-muted">
                      ({currentStudentRanking.presentCount || 0}/
                      {currentStudentRanking.totalLectures || 0} lectures)
                    </small>
                  </td>
                  <td className="text-center align-middle">
                    <Badge
                      bg={
                        currentStudentRanking.quizPassed
                          ? "success"
                          : currentStudentRanking.quizAttempted
                            ? "danger"
                            : "secondary"
                      }
                      className="px-3 py-2"
                    >
                      {currentStudentRanking.quizPassed ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <FaCheckCircle size={14} /> Passed
                        </span>
                      ) : currentStudentRanking.quizAttempted ? (
                        <span className="d-inline-flex align-items-center gap-1">
                          <FaTimesCircle size={14} /> Failed
                        </span>
                      ) : (
                        "Not Attempted"
                      )}
                    </Badge>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // For the detailed rankings table, use the rank from API directly
  const rankedRankings = useMemo(() => {
    // The rankings array from API already contains the rank property for each student
    // No need to recalculate rank using index
    return rankings;
  }, [rankings]);

  /* Infinite scroll, the shape ListAttendance uses: the page is the
     scroller and the foot of the list reaching the viewport asks for the
     next page. This list IS server-paged.

     It bumps apiPagination.page, which is what the fetch effect above
     watches. It used to bump tableCurrentPage -- a number nothing reads
     once the pager is hidden -- so scrolling to the bottom fired the
     handler, moved a counter, and sent no request at all.

     Placed below rankedRankings on purpose — it reads that const, and a
     hook above it would fail with "Cannot access before initialization".
     Neither eslint nor the bundler catches that. */
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loadingRanking || loadingMoreRanking) return;
      if (rankedRankings.length >= (apiPagination.total || 0)) return;
      setApiPagination((prev) => ({ ...prev, page: prev.page + 1 }));
      setTableCurrentPage((pageNow) => pageNow + 1);
    },
    [
      rankedRankings.length,
      apiPagination.total,
      loadingRanking,
      loadingMoreRanking,
    ],
  );

  /* Five columns and no more; email, the present/total split and the
     attempted count are in the popup, where there is room for them. */
  const rankingHeaders = [
    {
      title: "Rank",
      prop: "rank",
      sortable: true,
      cell: (row) => {
        const tone =
          row.rank === 1
            ? " pgm-pill--good"
            : row.rank <= 3
              ? " pgm-pill--good"
              : "";
        return row.rank <= 3 ? (
          <span className={`pgm-pill${tone}`}>#{row.rank}</span>
        ) : (
          <span className="pg-strong">#{row.rank}</span>
        );
      },
    },
    {
      title: "Student",
      prop: "studentName",
      searchable: true,
      sortable: true,
      cell: (row) => <span className="pg-strong">{row.studentName || "—"}</span>,
    },
    {
      title: "Attendance",
      prop: "attendancePercentage",
      sortable: true,
      cell: (row) =>
        row.attendancePercentage != null ? (
          <span className="pg-strong">{row.attendancePercentage}%</span>
        ) : (
          <span className="pg-none">—</span>
        ),
    },
    {
      title: "Avg quiz score",
      prop: "avgQuizScore",
      sortable: true,
      cell: (row) =>
        row.avgQuizScore != null ? (
          <span className="pg-strong">{row.avgQuizScore}</span>
        ) : (
          <span className="pg-none">—</span>
        ),
    },
    {
      title: "Quizzes passed",
      prop: "quizPassed",
      sortable: true,
      cell: (row) => (
        <span className="pg-date">
          {row.quizPassed ?? 0} of {row.quizAttempted ?? 0}
        </span>
      ),
    },
  ];

  const handleTablePageChange = (page) => {
    setTableCurrentPage(page);
  };

  const handleTableRowsPerPageChange = (newLimit) => {
    setTableRecordsPerPage(Number(newLimit));
    setTableCurrentPage(1);
  };

  const handleTableSearchChange = (term) => {
    setTableSearchTerm(term);
    setTableCurrentPage(1);
  };

  /* Escape shuts the record popup, the same as clicking the veil. Part
     of the frame every popup in this kit shares, and the one part of it
     this page had not implemented. */
  useEffect(() => {
    if (!openRow) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpenRow(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [openRow]);

  /* ── The record popup ──────────────────────────────────────────────
     Built to the plan the attendance list's popup sets and the student
     users list follows: the person is the title, a .pgm-ident dl of
     labelled facts sits under it, the body is .pgm-section blocks each
     led by a .pgm-section-title with a .pgm-table for anything holding
     more than one value, and the footer's only filled button is Close.

     What this replaced put the identity in a .pgm-sub sentence and the
     record in a .pgm-facts list of .pg-label spans -- pg-label being
     the filter strip's label, borrowed into a dialog -- and left Close
     as an outline button at the left of the footer.
     ---------------------------------------------------------------- */
  const detail = openRow
    ? (() => {
        const r = openRow;
        const top3 = r.rank <= 3;
        const batchLabel = selectedBatchOption?.label || "—";

        const totalLectures = r.totalLectures ?? 0;
        const presentCount = r.presentCount ?? 0;

        /* quizAttempted and quizPassed are BOOLEANS on a ranking row
           (ranking.service.js), which is what the counts here are read
           from instead: attemptedQuizzes out of totalQuizzes, with
           quizStatus as the verdict. The two rows this replaced printed
           the booleans straight -- React renders `true` as nothing, so
           both had been showing blank since they were written. */
        const attempted = r.attemptedQuizzes ?? 0;
        const totalQuizzes = r.totalQuizzes ?? 0;
        const quizStatus =
          r.quizStatus ||
          (r.quizAttempted ? (r.quizPassed ? "Passed" : "Failed") : "Not attempted");

        const attendanceRows = [
          ["Classes attended", `${presentCount} of ${totalLectures}`],
          ["Classes missed", String(Math.max(totalLectures - presentCount, 0))],
          ["Attendance", formatAttendance(r.attendancePercentage)],
        ];

        /* The result is a cell like any other -- a pill in the Value
           column rather than a line of its own under the table, which
           would have needed a class the stylesheet does not have. */
        const quizRows = [
          ["Quizzes attempted", `${attempted} of ${totalQuizzes}`],
          ["Average score", formatQuizScore(r.avgQuizScore)],
          [
            "Result",
            <span
              className={`pgm-pill ${
                r.quizPassed
                  ? "pgm-pill--good"
                  : r.quizAttempted
                    ? "pgm-pill--bad"
                    : ""
              }`}
            >
              {quizStatus}
            </span>,
          ],
        ];

        /* # / Detail / Value, inside the scroller the shared .pgm-table
           needs: below 576px it hides its third column, which in a table
           of this shape is the value itself. */
        const factTable = (rows) => (
          <div className="pgm-tablescroll">
            <table className="pgm-table pgm-table--values">
              <thead>
                <tr>
                  <th className="pgm-num">#</th>
                  <th>Detail</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, value], i) => (
                  <tr key={label}>
                    <td className="pgm-num">{i + 1}</td>
                    <td>{label}</td>
                    <td>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

        return (
          <div
            className="pg-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rank-detail-title"
            onClick={() => setOpenRow(null)}
          >
            <div
              className="pg-modal-panel pg-modal-panel--wide"
              onClick={(e) => e.stopPropagation()}
            >
              {/* The student is what the record is about, so they are the
                  title -- in full, never clipped the way the table clips
                  it. Rank and batch are what identifies which record of
                  theirs this is. */}
              <header className="pgm-head">
                <div className="pgm-head-main">
                  <h2 className="pgm-batch" id="rank-detail-title">
                    {r.studentName || "Student"}
                  </h2>
                  <dl className="pgm-ident">
                    <div className="pgm-ident-row">
                      <dt>Rank</dt>
                      <dd>#{r.rank}</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Batch</dt>
                      <dd>{batchLabel}</dd>
                    </div>
                    <div className="pgm-ident-row">
                      <dt>Quiz result</dt>
                      <dd>{quizStatus}</dd>
                    </div>
                  </dl>
                </div>
                <button
                  type="button"
                  className="pg-modal-close"
                  onClick={() => setOpenRow(null)}
                  aria-label="Close"
                >
                  &times;
                </button>
              </header>

              <div className="pg-modal-body">
                {/* The three figures the ranking is actually made of,
                    before the rows they are worked out from. */}
                <div className="pgm-tiles">
                  <div className={`pgm-tile${top3 ? " pgm-tile--good" : ""}`}>
                    <span>Rank</span>
                    <b>#{r.rank}</b>
                  </div>
                  <div className="pgm-tile">
                    <span>Attendance</span>
                    <b>{formatAttendance(r.attendancePercentage)}</b>
                  </div>
                  <div className="pgm-tile">
                    <span>Avg quiz score</span>
                    <b>{formatQuizScore(r.avgQuizScore)}</b>
                  </div>
                </div>

                <section className="pgm-section">
                  <h3 className="pgm-section-title">
                    Attendance
                    <span className="pgm-section-count">{totalLectures}</span>
                  </h3>
                  {totalLectures === 0 ? (
                    <p className="pgm-empty">
                      No lectures have been held for this batch yet.
                    </p>
                  ) : (
                    factTable(attendanceRows)
                  )}
                </section>

                <section className="pgm-section">
                  <h3 className="pgm-section-title">
                    Quizzes
                    <span className="pgm-section-count">{totalQuizzes}</span>
                  </h3>
                  {totalQuizzes === 0 ? (
                    <p className="pgm-empty">
                      No quizzes have been assigned to this batch yet.
                    </p>
                  ) : (
                    factTable(quizRows)
                  )}
                </section>
              </div>

              {/* Nothing can be done to a ranking from here -- it is
                  worked out, not edited -- so Close is the whole footer.
                  .pg-modal-foot > :only-child keeps it on the right. */}
              <footer className="pg-modal-foot">
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setOpenRow(null)}
                >
                  Close
                </button>
              </footer>
            </div>
          </div>
        );
      })()
    : null;

  /* A bar per student on this page: attendance beside average quiz
     score, so the two halves of the ranking can be compared at a glance
     instead of inferred from two number columns.

     Only what is on screen is charted — charting all 300 of a big batch
     would be unreadable, and the reader is looking at this page. */
  const chartRows = rankedRankings.slice(0, 12);

  /* Series slots 1 and 2 of the chart theme -- the palette's accent, then
     the first fixed hue that stays distinguishable from it. The slot follows
     the measure: attendance is always slot 1 and quiz score always slot 2,
     and the HTML legend above the plot reads the same two slots. */
  const chartSeries = [
    { label: "Attendance %", value: (r) => r.attendancePercentage ?? 0, colour: chartTheme.series[0] },
    { label: "Avg quiz score", value: (r) => r.avgQuizScore ?? 0, colour: chartTheme.series[1] },
  ];

  const chartData = {
    labels: chartRows.map((r) => r.studentName || "—"),
    datasets: chartSeries.map((s) => ({
      label: s.label,
      data: chartRows.map(s.value),
      backgroundColor: s.colour,
      hoverBackgroundColor: s.colour,
      // Rounded at the value, square at the baseline it grows from, and
      // never thicker than 24px however few students are on the page.
      borderRadius: 4,
      borderSkipped: "start",
      maxBarThickness: 24,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        ...chartTheme.tooltip,
        // Two series share the tooltip, so each row carries its swatch.
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
      },
    },
    scales: {
      x: chartTheme.categoryAxis({
        border: { display: false },
        ticks: {
          color: chartTheme.ink.faint,
          font: { size: 11 },
          maxRotation: 0,
          /* Long names would stack the axis three deep. */
          callback(value) {
            const name = String(this.getLabelForValue(value) || "");
            return name.length > 12 ? `${name.slice(0, 12)}…` : name;
          },
        },
      }),
      y: chartTheme.valueAxis({
        beginAtZero: true,
        ticks: { font: { size: 11 } },
      }),
    },
  };

  /* The three at the top of the batch, as the tiles the popups use —
     rather than a bespoke podium this project has nowhere else. */
  const podium =
    topThree && topThree.length > 0 ? (
      <div className="pgm-tiles">
        {[...topThree]
          .sort((a, b) => a.rank - b.rank)
          .slice(0, 3)
          .map((t) => (
            <div
              className={`pgm-tile${t.rank === 1 ? " pgm-tile--good" : ""}`}
              key={t.studentId || t.rank}
            >
              <span>
                {t.rank === 1 ? "1st" : t.rank === 2 ? "2nd" : "3rd"} · {t.studentName}
              </span>
              <b>{t.avgQuizScore != null ? t.avgQuizScore : "—"}</b>
            </div>
          ))}
      </div>
    ) : null;

  return (
    <div className="pg">
        {/* Which batch is being ranked. A student sees their own batch
            without a picker; staff choose one.

            Before a batch is chosen the picker sits inside the gate --
            the same card /student-profile, /student-quizzes and
            /student-mock-interview show -- rather than a filter strip at
            the top with a "nothing selected" notice far below it
            pointing back up. Afterwards it becomes the strip. */}
        {!isStudentView && !selectedBatchId && (
          <div className="pg-gate">
            <span className="pg-gate-mark">
              <FaTrophy size={20} aria-hidden="true" />
            </span>
            <h1 className="pg-gate-title">Pick a batch</h1>
            <p className="pg-gate-text">
              Choose a batch to see how its students are ranked.
            </p>

            <div className="pg-gate-field">{batchPicker}</div>

            <span className="pg-gate-tip">
              <FaInfoCircle size={13} aria-hidden="true" />
              Search by batch name or course.
            </span>
          </div>
        )}

        {!isStudentView && selectedBatchId && (
          <FilterSheet label="Filter rankings">
            <div className="pg-filters-row">
              <div className="pg-field pg-field--grow">
                <label className="pg-label">Batch</label>
                {batchPicker}
              </div>

              <button
                type="button"
                className="pg-btn"
                onClick={() => handleBatchChange([])}
              >
                <FaTimes size={12} aria-hidden="true" />
                Clear
              </button>
            </div>
          </FilterSheet>
        )}

        {error ? (
          <div className="pg-state">
            <span className="pg-state-icon">
              <FaTrophy size={26} aria-hidden="true" />
            </span>
            <h3 className="pg-state-title">Unable to load rankings</h3>
            <p className="pg-state-text">{error}</p>
          </div>
        ) : !selectedBatchId ? null : (
          <>
            {/* Where the batch stands, before the table of who is where. */}
            <div className="ov-tiles">
              <div className="ov-tile">
                <span className="ov-tile-label">Batch</span>
                <span className="rk-batch-name">
                  {selectedBatchOption?.label || "—"}
                </span>
              </div>
              <div className="ov-tile">
                <span className="ov-tile-label">Students ranked</span>
                <span className="ov-tile-value">{apiPagination.total || 0}</span>
              </div>
              {currentStudentRanking && (
                <div className="ov-tile">
                  <span className="ov-tile-label">Your rank</span>
                  <span className="ov-tile-value">#{currentStudentRanking.rank}</span>
                </div>
              )}
            </div>

            {podium}

            {chartRows.length > 0 && (
              <section className="bp-chart">
                <header className="bp-chart-head">
                  <div>
                    <h2 className="bp-chart-title">Attendance against quiz score</h2>
                    <p className="bp-chart-sub">
                      The {chartRows.length} student
                      {chartRows.length === 1 ? "" : "s"} on this page
                    </p>
                  </div>
                  <div className="bp-legend">
                    <span>
                      <span
                        className="bp-legend-dot"
                        style={{ background: chartSeries[0].colour }}
                        aria-hidden="true"
                      />{" "}
                      Attendance %
                    </span>
                    <span>
                      <span
                        className="bp-legend-dot"
                        style={{ background: chartSeries[1].colour }}
                        aria-hidden="true"
                      />{" "}
                      Avg quiz score
                    </span>
                  </div>
                </header>
                <div className="bp-chart-canvas">
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </section>
            )}

            <div className="td-list td-list--rank" ref={listRef}>
              <NewCustomDataTable
                data={rankedRankings}
                headers={rankingHeaders}
                totalRecords={apiPagination.total}
                currentPage={tableCurrentPage}
                recordsPerPage={tableRecordsPerPage}
                onPageChange={handleTablePageChange}
                onRowsPerPageChange={handleTableRowsPerPageChange}
                searchTerm={tableSearchTerm}
                onSearchChange={handleTableSearchChange}
                onSelectedFieldChange={setTableSelectedField}
                searchableHeaders={[{ value: "studentName", label: "Student Name" }]}
                selectedField={tableSelectedField}
                serverSide={true}
                addButton={false}
                exportButton={false}
                loading={loadingRanking}
                loadingMore={loadingMoreRanking}
                showToolbar={false}
                showPagination={false}
                showFooter={false}
                onRowClick={(row) => setOpenRow(row)}
                /* Every cell wraps its text in a span, so the default
                   ['TR','TD'] would leave the text itself unclickable. */
                validRowClickTagNames="*"
                rowProps={(row) => ({
                  tabIndex: 0,
                  role: "button",
                  "aria-label": `Open ranking for ${row.studentName || "this student"}`,
                  onKeyDown: (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenRow(row);
                    }
                  },
                })}
              />
            </div>

            {/* What the page buttons used to say, said once. Outside the
                list, as on the attendance page. */}
            <div className="pg-scroll-foot" ref={listEndRef}>
                {rankedRankings.length < (apiPagination.total || 0) ? (
                  <>
                    <span className="pg-scroll-count">
                      {rankedRankings.length} of {apiPagination.total}
                    </span>
                    {loadingMoreRanking && (
                      <span className="pg-spinner" aria-hidden="true" />
                    )}
                  </>
                ) : (
                  (apiPagination.total || 0) > 0 && (
                    <span className="pg-scroll-count">
                      All {apiPagination.total} student
                      {apiPagination.total === 1 ? "" : "s"} loaded
                    </span>
                  )
                )}
            </div>
          </>
        )}

      {detail}
    </div>
  );
};

export default RankingDashboard;
